import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Copy, FileSpreadsheet, IndianRupee, Save, Target, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "./common";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Progress } from "./ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtINR, downloadBlob } from "../lib/api";
import { TARGET_STATUS_META } from "./SupervisorTargetDialog";
import { TargetProposalsPanel } from "./TargetProposalsPanel";
import { StaffLabel } from "./StaffAvatar";
import { ReadOnlyNotice } from "./FeatureGate";
import { PlaceholderBadge } from "./PlaceholderCollectors";
import { DateRangePicker } from "./DateRangePicker";
import { MonthlyTargetsDialog } from "./MonthlyTargetsDialog";
import { presetRange, rangeParams, singleMonthOf, isFullMonths, prevMonth, monthLabel, rangeLabel, monthsInRange } from "../lib/dateRange";

const StatusBadge = ({ status }) => {
  const m = TARGET_STATUS_META[status] || TARGET_STATUS_META.no_target;
  return <Badge variant="outline" className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
};

/** "Same for all" — fills every collector input in a supervisor's group with one amount. */
function GroupApply({ code, group, onApply }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const gid = group.supervisor_id || "unmapped";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="link" size="sm" className="h-auto p-0 ml-3 text-[11px]" data-testid={`collector-targets-apply-group-${gid}`}>
          Same for all
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-2" data-testid={`collector-targets-apply-popover-${gid}`}>
        <p className="text-xs">Same target for all <b>{group.collectors.length}</b> collectors under <StaffLabel code={code} userId={group.supervisor_id} name={group.supervisor_name} /> (₹). Blank clears.</p>
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); onApply(value.trim()); setOpen(false); setValue(""); }}>
          <Input type="number" min="0" step="100" value={value} onChange={(e) => setValue(e.target.value)} className="h-8" autoFocus
            data-testid={`collector-targets-apply-input-${gid}`} />
          <Button type="submit" size="sm" className="h-8" data-testid={`collector-targets-apply-submit-${gid}`}>Apply</Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Users & Staff → "Collector targets": Admin-owned monthly targets for every tax collector, grouped by supervisor.
 * Phase 175: a From/To range (default: this month) drives every figure — monthly targets are pro-rated by the days of
 * each month inside the range; collections / expected / achievement are computed inside the range. Editing stays
 * monthly: a range inside ONE month edits that month inline; a multi-month range opens a month-by-month editor.
 */
export function CollectorTargetsTab({ code, readOnly = false }) {
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});   // {collector_id: string} — single-month mode only
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [monthsFor, setMonthsFor] = useState(null);   // collector row → month-by-month editor (multi-month range)

  const month = singleMonthOf(range);
  const partial = !isFullMonths(range);
  const label = rangeLabel(range);
  const monthKeys = useMemo(() => monthsInRange(range), [range]);

  const monthlyOf = (c) => ((c.months || [])[0] || {}).target;   // single-month mode: the stored monthly value
  const load = useCallback(() => {
    setData(null);
    return api.get(`/t/${code}/collector-targets`, { params: rangeParams(range) }).then((r) => {
      setData(r.data);
      const e = {};
      (r.data.groups || []).forEach((g) => g.collectors.forEach((c) => { const v = monthlyOf(c); e[c.id] = v != null ? String(v) : ""; }));
      setEdits(e);
    });
  }, [code, range]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const allRows = useMemo(() => (data?.groups || []).flatMap((g) => g.collectors), [data]);
  const dirty = useMemo(() => (month ? allRows.filter((c) => (edits[c.id] ?? "") !== (monthlyOf(c) != null ? String(monthlyOf(c)) : "")) : []), [allRows, edits, month]);

  const saveAll = async () => {
    if (dirty.length === 0 || !month) return;
    setBusy(true);
    try {
      const items = dirty.map((c) => ({ collector_id: c.id, target_amount: edits[c.id] === "" ? null : Number(edits[c.id]) }));
      const r = await api.put(`/t/${code}/collector-targets/bulk`, { month, items });
      const bits = [];
      if (r.data.saved) bits.push(`${r.data.saved} set`);
      if (r.data.cleared) bits.push(`${r.data.cleared} cleared`);
      if (r.data.superseded_proposals) bits.push(`${r.data.superseded_proposals} pending proposal${r.data.superseded_proposals === 1 ? "" : "s"} closed`);
      toast.success(`Collector targets saved for ${monthLabel(month, true)} — ${bits.join(", ")}`);
      await load();
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copyPrevious = async () => {
    if (!month) return;
    const pm = prevMonth(month);
    try {
      const r = await api.get(`/t/${code}/collector-targets`, { params: { month: pm } });
      const rows = (r.data.groups || []).flatMap((g) => g.collectors).filter((c) => c.target != null);
      if (rows.length === 0) { toast.info(`No collector targets were set in ${monthLabel(pm, true)}`); return; }
      setEdits((e) => { const n = { ...e }; rows.forEach((c) => { if (c.id in n) n[c.id] = String(c.target); }); return n; });
      toast.success(`Copied ${rows.length} collector target${rows.length === 1 ? "" : "s"} from ${monthLabel(pm, true)} — review and click Save all`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyToGroup = (g, value) => {
    setEdits((e) => { const n = { ...e }; g.collectors.forEach((c) => { n[c.id] = value; }); return n; });
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      await downloadBlob(`/t/${code}/collector-targets/export?from=${range.from}&to=${range.to}`, `collector-targets_${range.from}_to_${range.to}.xlsx`);
      toast.success(`Collector targets exported (${label})`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const t = data?.totals || {};
  // Phase 165: "Collected" is the range's unified total (== MIS Reports "All receipts"); `others` explains the
  // part that is not credited to the collectors listed on this page (supervisors' receipts, imports, …)
  const othersBuckets = (t.others?.buckets || []).filter((b) => b.receipts);
  const n = (v) => Number(v || 0).toLocaleString("en-IN");
  const pace = data ? { elapsed: data.elapsed_days, days: data.days_in_month } : null;
  const rangeSuffix = !month || partial ? " (range)" : "";
  const perMonth = (c) => (c.months || []).filter((m) => m.target != null).map((m) => `${m.label}: ${fmtINR(m.target)}`).join(" · ");

  return (
    <div className="space-y-4" data-testid="collector-targets-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRangePicker value={range} onChange={setRange} pace={pace} testIdPrefix="collector-targets-range" />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={exporting || !data} data-testid="collector-targets-export">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting..." : "Export Excel"}
          </Button>
          {!readOnly && month && <>
            <Button variant="outline" size="sm" onClick={copyPrevious} disabled={busy} data-testid="collector-targets-copy-previous">
              <Copy className="h-4 w-4 mr-1.5" /> Copy from {monthLabel(prevMonth(month))}
            </Button>
            <Button size="sm" onClick={saveAll} disabled={busy || dirty.length === 0} data-testid="collector-targets-save-all">
              <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : dirty.length ? `Save all (${dirty.length})` : "Save all"}
            </Button>
          </>}
        </div>
      </div>
      {readOnly && <ReadOnlyNotice what="collector targets" testId="collector-targets-readonly-notice" />}

      {!month && (
        <p className="text-xs rounded-md border bg-muted/40 px-3 py-2 flex items-start gap-2" data-testid="collector-targets-multi-month-note">
          <CalendarDays className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <span>
            <b>{label}</b> spans {monthKeys.length} months. Targets below are the monthly targets <b>pro-rated by days</b> inside the range
            (e.g. 15 Sep – 15 Oct = Sep × 16/30 + Oct × 15/31); collections, expected and achievement are for the range only.
            {!readOnly && <> Targets are still set per month — use <b>Set monthly targets</b> on a row.</>}
          </span>
        </p>
      )}
      {month && partial && data && (
        <p className="text-xs rounded-md border bg-muted/40 px-3 py-2 flex items-start gap-2" data-testid="collector-targets-partial-note">
          <CalendarDays className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <span>
            <b>{label}</b> covers {data.days_in_month} of {(data.range?.months?.[0] || {}).days_in_month} days of {monthLabel(month, true)}. The inputs edit the
            <b> full monthly target</b>; the figure beside each shows how much of it falls inside the range and the collections made in it.
          </span>
        </p>
      )}

      {!data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Total collector targets{rangeSuffix}</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="collector-targets-total">{fmtINR(t.target)}</p>
              <p className="text-[11px] text-muted-foreground">
                {t.with_target} of {t.collectors} collectors have a target
                {t.expected_to_date != null && <> · <span className="tabular-nums" data-testid="collector-targets-expected">expected by today {fmtINR(t.expected_to_date)}</span></>}
              </p>
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" /> Collected · {label}</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="collector-targets-collected"
                title="Every successful receipt dated inside the range — supervisors' own receipts and imported receipts included. Same as “All receipts” in MIS Reports and the Collections register.">
                {fmtINR(t.collected)}
              </p>
              <p className="text-[11px] text-muted-foreground tabular-nums" data-testid="collector-targets-collected-sub">
                {n(t.collected_receipts)} receipt{t.collected_receipts === 1 ? "" : "s"} · all receipts, same as MIS Reports
              </p>
              {t.credited != null && Math.abs((t.credited || 0) - (t.collected || 0)) >= 0.01 && (
                <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5" data-testid="collector-targets-credited">
                  <span className="font-medium text-foreground">{fmtINR(t.credited)}</span> credited to the collectors below{othersBuckets.length > 0 && " · "}
                  {othersBuckets.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="underline underline-offset-2 hover:text-foreground" data-testid="collector-targets-others-toggle">
                          {fmtINR(t.others?.amount)} other
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-80 space-y-1.5 text-xs" data-testid="collector-targets-others-popover">
                        <p className="font-medium">Receipts not credited to a listed collector</p>
                        <ul className="space-y-1">
                          {othersBuckets.map((b) => (
                            <li key={b.key} className="flex items-start justify-between gap-3" data-testid={`collector-targets-others-${b.key}`}>
                              <span className="min-w-0">
                                {b.label}
                                {(b.rows || []).length > 0 && (
                                  <span className="block text-[11px] text-muted-foreground">
                                    {b.rows.slice(0, 4).map((r) => `${r.name}${r.employee_id ? ` (${r.employee_id})` : ""}: ${fmtINR(r.amount)}`).join(" · ")}{b.rows.length > 4 ? ` +${b.rows.length - 4} more` : ""}
                                  </span>
                                )}
                              </span>
                              <span className="tabular-nums whitespace-nowrap font-medium">{fmtINR(b.amount)} <span className="text-muted-foreground font-normal">· {n(b.receipts)}</span></span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-muted-foreground pt-1 border-t">
                          {fmtINR(t.credited)} credited + {fmtINR(t.others?.amount)} other = {fmtINR(t.collected)} all receipts{t.reconciles === false ? " — check totals" : ""}.
                        </p>
                      </PopoverContent>
                    </Popover>
                  )}
                </p>
              )}
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Achievement</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="collector-targets-achievement">{t.achievement_pct != null ? `${t.achievement_pct}%` : "—"}</p>
              <p className="text-[11px] text-muted-foreground tabular-nums" data-testid="collector-targets-achievement-sub">
                {t.achievement_pct != null
                  ? <>all receipts vs total targets{t.credited_achievement_pct != null && t.credited_achievement_pct !== t.achievement_pct ? ` · ${t.credited_achievement_pct}% from the collectors below` : ""}</>
                  : "set collector targets to track achievement"}
              </p>
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Pending proposals</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="collector-targets-pending">{t.pending_proposals || 0}</p>
              <p className="text-[11px] text-muted-foreground">from supervisors with “View & edit”{!month ? ` · ${monthKeys.length} months` : ""}</p>
            </CardContent></Card>
          </div>

          <TargetProposalsPanel key={refreshKey} code={code} month={month || undefined} months={month ? undefined : monthKeys} periodLabel={label}
            pendingOnly compact onChanged={() => load()} readOnly={readOnly} />

          {allRows.length === 0 ? (
            <EmptyState title="No active tax collectors" subtitle="Add tax collectors in the Staff directory first." />
          ) : (
            <Card className="shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                <Table data-testid="collector-targets-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collector</TableHead>
                      <TableHead className="min-w-[180px]">{month ? `Target (₹) · ${monthLabel(month)}` : "Target (₹, range)"}</TableHead>
                      <TableHead className="min-w-[220px]">Collected · {label}</TableHead>
                      <TableHead className="text-right">Expected by today</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.groups.map((g) => (
                      <React.Fragment key={g.supervisor_id || "unmapped"}>
                        <TableRow className="bg-muted/40 hover:bg-muted/40" data-testid={`collector-targets-group-${g.supervisor_id || "unmapped"}`}>
                          <TableCell colSpan={2} className="py-2">
                            <p className="text-xs font-semibold flex items-center gap-2">
                              <Users className="h-3.5 w-3.5" /> {g.supervisor_id ? <StaffLabel code={code} userId={g.supervisor_id} name={g.supervisor_name} /> : g.supervisor_name}
                              {g.zone_names?.length > 0 && <span className="text-muted-foreground font-normal">· {g.zone_names.join(", ")}</span>}
                              <span className="text-muted-foreground font-normal">· {g.collectors.length} collector{g.collectors.length === 1 ? "" : "s"}</span>
                            </p>
                          </TableCell>
                          <TableCell colSpan={2} className="py-2 text-right text-xs">
                            <span className="text-muted-foreground">Team target</span> <b className="tabular-nums">{fmtINR(g.team_target)}</b>
                            <span className="text-muted-foreground"> · collected</span> <b className="tabular-nums">{fmtINR(g.team_collected)}</b>
                            {g.achievement_pct != null && <span className="text-muted-foreground"> · {g.achievement_pct}%</span>}
                            {g.collectors.length > 1 && !readOnly && month && (
                              <GroupApply code={code} group={g} onApply={(v) => applyToGroup(g, v)} />
                            )}
                          </TableCell>
                        </TableRow>
                        {g.collectors.length === 0 && (
                          <TableRow><TableCell colSpan={4} className="py-2 text-xs text-muted-foreground pl-8">No tax collectors mapped to this supervisor.</TableCell></TableRow>
                        )}
                        {g.collectors.map((c) => {
                          const isDirty = month && (edits[c.id] ?? "") !== (monthlyOf(c) != null ? String(monthlyOf(c)) : "");
                          const pct = c.target ? Math.min(100, Math.round(c.collected_mtd * 100 / c.target)) : 0;
                          return (
                            <TableRow key={c.id} className={isDirty ? "bg-primary/[0.04]" : ""} data-testid={`collector-target-row-${c.email}`}>
                              <TableCell className="pl-8">
                                <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                                  <StaffLabel code={code} userId={c.id} name={c.name} empCode={c.employee_id} />
                                  {c.is_placeholder && <PlaceholderBadge testId={`collector-target-placeholder-${c.email}`} />}
                                  {c.is_sub && <Badge variant="outline" className="text-[10px]">Sub-user of {c.parent_user_name}</Badge>}
                                </div>
                                <p className="text-[11px] text-muted-foreground">
                                  {c.ward_names.length ? `Wards: ${c.ward_names.join(", ")}` : "No wards assigned"}
                                  {c.set_by_name && <> · set by <StaffLabel code={code} userId={c.set_by} name={c.set_by_name} /></>}
                                </p>
                                {c.pending_proposal && (
                                  <p className="text-[11px] text-amber-800 mt-0.5" data-testid={`collector-target-pending-${c.email}`}>
                                    Proposed {fmtINR(c.pending_proposal.proposed_amount)} for {monthLabel(c.pending_proposal.month, true)} by <StaffLabel code={code} userId={c.pending_proposal.proposed_by} name={c.pending_proposal.proposed_by_name} /> — approve above or {month ? "type an amount to override" : "set the month's target to override"}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell>
                                {month ? (
                                  <div className="space-y-1">
                                    <Input type="number" min="0" step="100" value={edits[c.id] ?? ""} placeholder="—" disabled={readOnly}
                                      onChange={(ev) => setEdits((e) => ({ ...e, [c.id]: ev.target.value }))}
                                      className="h-9" data-testid={`collector-target-input-${c.email}`} />
                                    {partial && c.target != null && (
                                      <p className="text-[11px] text-muted-foreground tabular-nums" data-testid={`collector-target-range-${c.email}`}>{fmtINR(c.target)} in range</p>
                                    )}
                                  </div>
                                ) : (
                                  <div className="space-y-0.5" data-testid={`collector-target-range-${c.email}`}>
                                    <p className="tabular-nums text-sm font-medium">{c.target != null ? fmtINR(c.target) : <span className="text-muted-foreground">—</span>}</p>
                                    <p className="text-[11px] text-muted-foreground truncate max-w-[200px]" title={perMonth(c)}>{perMonth(c) || "no monthly targets"}</p>
                                    {!readOnly && (
                                      <Button variant="link" size="sm" className="h-auto p-0 text-[11px]" onClick={() => setMonthsFor(c)}
                                        data-testid={`collector-target-edit-months-${c.email}`}>
                                        <CalendarDays className="h-3 w-3 mr-1" /> Set monthly targets
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between gap-2 text-sm">
                                    <span className="tabular-nums font-medium">{fmtINR(c.collected_mtd)} <span className="text-[11px] text-muted-foreground font-normal">· {c.receipts} receipts</span></span>
                                    <StatusBadge status={c.status} />
                                  </div>
                                  {c.target > 0 && (
                                    <div className="flex items-center gap-2">
                                      <Progress value={pct} className="h-1.5" />
                                      <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">{c.achievement_pct}%</span>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right tabular-nums text-sm" data-testid={`collector-target-expected-${c.email}`}>{c.expected_to_date != null ? fmtINR(c.expected_to_date) : "—"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
      <p className="text-xs text-muted-foreground">
        Collector targets are set by the Municipal Admin, <b>per calendar month</b>. Supervisors see them read-only on their Team Scorecard; give a supervisor
        <b> View & edit</b> on the Team Scorecard module (Role Access, or per user from the Staff directory) to let them <b>propose</b> targets
        for your approval, or <b>Full access</b> to let them set targets directly. <b>Expected by today</b> = range target × days elapsed ÷ days in range. Blank or 0 clears a target.
      </p>

      {monthsFor && (
        <MonthlyTargetsDialog code={code} kind="collector" row={monthsFor} rangeLabel={label} open={!!monthsFor}
          onOpenChange={(v) => !v && setMonthsFor(null)} onSaved={() => { load().catch((e) => toast.error(errMsg(e))); setRefreshKey((k) => k + 1); }} />
      )}
    </div>
  );
}
